import { useState, useRef, useEffect } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { useJobChat, useSendChatMessage } from "@/lib/api";

interface ChatPanelProps {
  jobId: string;
  /** Report type of the currently open tab — sent as active_tab for scope hints. */
  activeTab?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ChatPanel({ jobId, activeTab, isOpen, onClose }: ChatPanelProps) {
  const { data: history } = useJobChat(isOpen ? jobId : undefined);
  const sendMutation = useSendChatMessage();
  const [input, setInput] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const messages = history ?? [];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length, pending, sendMutation.isPending]);

  if (!isOpen) return null;

  const remaining = sendMutation.data?.remaining_messages;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sendMutation.isPending) return;
    setPending(text);
    setInput("");
    try {
      await sendMutation.mutateAsync({ jobId, message: text, activeTab });
    } catch {
      setInput(text); // restore so the user can retry
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="w-[360px] border-l bg-background flex flex-col h-full overflow-hidden flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">Ask AI</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !pending && (
          <p className="text-xs text-muted-foreground text-center py-6 leading-relaxed">
            Ask me to refine your reports — adjust the focus, fix a speaker name,
            change the style, or ask a question about the recording.
          </p>
        )}
        {messages.map((m) => (
          <ChatBubble
            key={m.id}
            role={m.role}
            content={m.content}
            action={m.action_taken}
            affected={m.affected_reports}
          />
        ))}
        {pending && <ChatBubble role="user" content={pending} />}
        {sendMutation.isPending && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t">
        {sendMutation.isError && (
          <p className="text-xs text-red-500 mb-2">
            {sendMutation.error?.message || "Failed to send message"}
          </p>
        )}
        <div className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Type a message…"
            rows={2}
            className="flex-1 text-xs px-3 py-2 rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sendMutation.isPending}
            className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md bg-blue-600 text-white disabled:opacity-40 hover:bg-blue-700 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {remaining !== undefined && (
          <p className="text-xs text-muted-foreground mt-2">
            {remaining} message{remaining === 1 ? "" : "s"} left
          </p>
        )}
      </div>
    </div>
  );
}

function ChatBubble({
  role,
  content,
  action,
  affected,
}: {
  role: string;
  content: string;
  action?: string | null;
  affected?: string[];
}) {
  const isUser = role === "user";
  const regenerated = action === "regenerated" || action === "corrected";
  return (
    <div className={isUser ? "flex justify-end" : "flex justify-start"}>
      <div
        className={
          "max-w-[85%] rounded-lg px-3 py-2 text-xs " +
          (isUser ? "bg-blue-600 text-white" : "bg-muted text-foreground")
        }
      >
        <p className="whitespace-pre-wrap leading-relaxed">{content}</p>
        {!isUser && regenerated && affected && affected.length > 0 && (
          <p className="text-[10px] mt-1.5 opacity-70">↻ Regenerating reports…</p>
        )}
      </div>
    </div>
  );
}
